/**
 * @fileoverview Componente de sincronização de dados entre Supabase e Zustand.
 *
 * Responsabilidades:
 * - Carrega membros da equipe, colunas do pipeline e custom field defs
 * - Rastreia progresso de sincronização (0% → 33% → 66% → 100%)
 * - Escuta eventos realtime (INSERT/UPDATE/DELETE) para leads e pipeline_columns
 * - Período de graça de 2s ao trocar de projeto (evita eventos stale)
 * - Expõe triggerDbSync() para refresh manual via evento global
 *
 * Também exporta useLeadsRealtime para colaboração em tempo real no pipeline.
 */
import { useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useStore } from '@/store/useStore';
import { dbLeadToDeal, DbLead } from '@/modules/crm/hooks/useSupabaseData';
import { Deal, TeamMember, KanbanColumnDef, CustomFieldDef } from '@/store/useStore';
import { DEFAULT_KANBAN_COLUMNS } from '@/lib/constants';
import { toast } from 'sonner';
import { playPlim } from '@/lib/sounds';
import { logger } from '@/lib/logger';
import { queryClient } from '@/App';
import { queryKeys } from '@/lib/queryKeys';

const DEFAULT_COLUMNS = DEFAULT_KANBAN_COLUMNS;

/**
 * Syncs leads, team members, kanban columns, and custom field defs
 * from the database into the Zustand store.
 */
export default function DbSync() {
  const activeProjectId = useStore(s => s.activeProjectId);
  const activeWorkspaceId = useStore(s => s.activeWorkspaceId);
  const setTeamMembers = useStore(s => s.setTeamMembers);
  const setKanbanColumns = useStore(s => s.setKanbanColumns);
  const setCustomFieldDefs = useStore(s => s.setCustomFieldDefs);
  const setSyncProgress = useStore(s => s.setSyncProgress);

  // Leads: React Query (`useDeals`) é a única fonte — a store não guarda
  // leads. `initDealsState` fica só para manter a sequência da barra de
  // progresso do sync inicial.
  const initDealsState = useCallback(async () => { /* leads: React Query */ }, []);

  // Fetch workspace members as team members
  const fetchTeamMembers = useCallback(async () => {
    if (!activeWorkspaceId) return;

    const { data, error } = await supabase
      .from('workspace_members')
      .select('user_id, role, profiles(id, name, email, avatar_url, role, tag)')
      .eq('workspace_id', activeWorkspaceId);

    if (error) {
      logger.error('DbSync: failed to fetch team members:', error.message);
      toast.error('Erro ao carregar equipe');
      return;
    }

    const members: TeamMember[] = (data || []).map((wm: any) => {
      const profile = wm.profiles;
      const profileRole = profile?.role || '';
      // Pass through commercial roles only (sdr, closer, gerente_comercial)
      // Members without commercial roles get empty string (not defaulted to sdr)
      const roles = profileRole.split(',').map((r: string) => r.trim()).filter(Boolean);
      const commercialRoles = roles.filter((r: string) => ['sdr', 'closer', 'gerente_comercial'].includes(r));
      const teamRole = commercialRoles.join(',');
      return {
        id: profile?.id || wm.user_id,
        name: profile?.name || 'Sem nome',
        role: teamRole,
        avatar: profile?.avatar_url || '',
        email: profile?.email || '',
      };
    });

    if (members.length > 0) {
      setTeamMembers(members);
    }
  }, [activeWorkspaceId, setTeamMembers]);

  // Fetch pipeline columns from DB
  const fetchKanbanColumns = useCallback(async () => {
    if (!activeProjectId) return;

    const { data, error } = await supabase
      .from('pipeline_columns')
      .select('*')
      .eq('project_id', activeProjectId)
      .order('sort_order', { ascending: true });

    if (error) {
      logger.error('DbSync: failed to fetch kanban columns:', error.message);
      toast.error('Erro ao carregar colunas do pipeline');
      return;
    }

    if (data && data.length > 0) {
      const cols: KanbanColumnDef[] = data.map((row: any) => ({
        id: row.column_key,
        label: row.label,
        order: row.sort_order,
        color: row.color || 'hsl(226, 100%, 51%)',
        wip_limit: row.wip_limit ?? null,
        sla_hours: row.sla_hours ?? null,
        // Semântica da coluna (ganho/perdido/reunião…). Null = ainda não
        // classificada → lib/stageSemantics infere pelo id/label.
        stageType: row.stage_type ?? null,
        // Regras de saida da etapa (campos obrigatorios / proximas etapas).
        exitRules: row.exit_rules ?? null,
      }));
      setKanbanColumns(cols);
    } else {
      // No columns in DB yet – seed defaults
      setKanbanColumns(DEFAULT_COLUMNS);
      // Persist defaults to DB
      const rows = DEFAULT_COLUMNS.map(c => ({
        project_id: activeProjectId,
        column_key: c.id,
        label: c.label,
        sort_order: c.order,
        color: c.color,
      }));
      await supabase.from('pipeline_columns').upsert(rows as any, { onConflict: 'project_id,column_key' });
    }
  }, [activeProjectId, setKanbanColumns]);

  // Fetch custom field definitions from DB
  const fetchCustomFieldDefs = useCallback(async () => {
    if (!activeProjectId) return;

    const { data, error } = await supabase
      .from('custom_field_defs')
      .select('*')
      .eq('project_id', activeProjectId)
      .order('sort_order', { ascending: true });

    if (error) {
      // Silent fail — data will be retried on next sync cycle
      return;
    }

    const defs: CustomFieldDef[] = (data || []).map((row: any) => ({
      id: row.id,
      label: row.label,
      type: row.field_type as 'text' | 'number' | 'select',
      options: row.options || undefined,
    }));
    setCustomFieldDefs(defs);
  }, [activeProjectId, setCustomFieldDefs]);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    // Run fetches with progress tracking
    setSyncProgress(0);
    (async () => {
      await initDealsState();
      if (!mountedRef.current) return;
      setSyncProgress(33);
      await fetchKanbanColumns();
      if (!mountedRef.current) return;
      setSyncProgress(66);
      await fetchCustomFieldDefs();
      if (!mountedRef.current) return;
      setSyncProgress(100);
    })();

    return () => {
      mountedRef.current = false;
    };
  }, [initDealsState, fetchKanbanColumns, fetchCustomFieldDefs]);

  useEffect(() => {
    mountedRef.current = true;
    fetchTeamMembers();
    return () => { mountedRef.current = false; };
  }, [fetchTeamMembers]);

  // Expose refetch globally via a custom event
  useEffect(() => {
    const handler = () => {
      initDealsState();
      fetchTeamMembers();
      fetchKanbanColumns();
      fetchCustomFieldDefs();
    };
    window.addEventListener('db-sync-refresh', handler);
    return () => window.removeEventListener('db-sync-refresh', handler);
  }, [initDealsState, fetchTeamMembers, fetchKanbanColumns, fetchCustomFieldDefs]);

  // Realtime subscriptions for leads (INSERT, UPDATE, DELETE)
  const initialLoadDone = useRef(false);
  useEffect(() => {
    // Reset grace period on every project switch so stale events from
    // the previous project are not applied to the new project's store.
    initialLoadDone.current = false;
    const timer = setTimeout(() => { initialLoadDone.current = true; }, 2000);

    if (!activeProjectId) return () => clearTimeout(timer);

    const channel = supabase
      .channel(`pipeline-rt-${activeProjectId}-${Math.random().toString(36).slice(2, 8)}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'leads',
          filter: `project_id=eq.${activeProjectId}`,
        },
        (payload) => {
          if (!initialLoadDone.current) return;
          const newLead = payload.new as DbLead;

          // Invalidar a query de todos os Deals do React Query para forçar recarregamento passivo
          queryClient.invalidateQueries({ queryKey: queryKeys.deals(activeProjectId) });
          // Lead de teste (QA/teste) já nasce na lixeira pela regra do banco
          // (trg_trash_test_leads) — não é "novo lead" para ninguém.
          if ((newLead as { deleted_at?: string | null }).deleted_at) return;
          // Só avisa de lead criado por OUTROS: o que este cliente criou já
          // está no cache (insert otimista com o mesmo id). `leads.created_by`
          // não existe — a checagem antiga nunca filtrava nada.
          const cached = queryClient.getQueryData<Deal[]>(queryKeys.deals(activeProjectId));
          if (cached?.some(d => d.id === newLead.id)) return;
          try { playPlim(0.5); } catch { /* empty */ }
          toast.success('Novo lead recebido!', {
            description: `${newLead.contact_name} — ${newLead.company}`,
            duration: 6000,
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'leads',
          filter: `project_id=eq.${activeProjectId}`,
        },
        (payload) => {
          if (!initialLoadDone.current) return;
          queryClient.invalidateQueries({ queryKey: queryKeys.deals(activeProjectId) });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'leads',
          filter: `project_id=eq.${activeProjectId}`,
        },
        (payload) => {
          if (!initialLoadDone.current) return;
          queryClient.invalidateQueries({ queryKey: queryKeys.deals(activeProjectId) });
        }
      )
      // Pipeline columns realtime
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'pipeline_columns',
          filter: `project_id=eq.${activeProjectId}`,
        },
        () => {
          if (!initialLoadDone.current) return;
          // Re-fetch all columns to keep order consistent
          fetchKanbanColumns();
        }
      )
      .subscribe();

    return () => {
      clearTimeout(timer);
      supabase.removeChannel(channel);
    };
  }, [activeProjectId, fetchKanbanColumns]);

  return null;
}

/** Trigger a refresh of all data from the database */
export function triggerDbSync() {
  window.dispatchEvent(new Event('db-sync-refresh'));
}

/**
 * ✅ Fix #4: Hook para realtime de leads/pipeline – colaboração em tempo real
 */
export function useLeadsRealtime(
  projectId: string | null,
  onInsert: (lead: any) => void,
  onUpdate: (lead: any) => void,
  onDelete: (id: string) => void,
) {
  const onInsertRef = useRef(onInsert);
  const onUpdateRef = useRef(onUpdate);
  const onDeleteRef = useRef(onDelete);
  useEffect(() => { onInsertRef.current = onInsert; }, [onInsert]);
  useEffect(() => { onUpdateRef.current = onUpdate; }, [onUpdate]);
  useEffect(() => { onDeleteRef.current = onDelete; }, [onDelete]);

  const initialLoadDoneRef = useRef(false);
  useEffect(() => {
    if (!projectId) return;
    initialLoadDoneRef.current = false;
    const initialDelay = setTimeout(() => {
      initialLoadDoneRef.current = true;
    }, 2000);
    const channel = supabase
      .channel(`leads-rt-${projectId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'leads', filter: `project_id=eq.${projectId}` }, (p) => {
        if (initialLoadDoneRef.current) onInsertRef.current(p.new);
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'leads', filter: `project_id=eq.${projectId}` }, (p) => {
        if (initialLoadDoneRef.current) onUpdateRef.current(p.new);
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'leads', filter: `project_id=eq.${projectId}` }, (p) => {
        if (initialLoadDoneRef.current) onDeleteRef.current((p.old as Pick<DbLead, 'id'>).id);
      })
      .subscribe();
    return () => {
      clearTimeout(initialDelay);
      supabase.removeChannel(channel);
    };
  }, [projectId]); // stable — only recreate if projectId changes
}

